import { isEmpty } from 'lodash';
import { getToken } from '../auth/api';
import { OWNER_ID, STAMP_METADATA_HEADER } from '../constants/constants';
import { getSafeExternalUrl } from '../links/linkUtils';
import { ATTACHMENT_DOWNLOAD_URL, DOWNLOAD_URL } from '../utils/download';

export const BASE_PATH = '/storage/v1/download/';

type AttachmentDownloadResponse = {
    isLink?: boolean;
    linkUrl?: string;
};

export type FetchAttachmentUrl = (
    attachmentId: string,
    activeOrganizationAccount: string,
    stampDocument?: boolean
) => Promise<string>;

export const getAttachmentDownloadLinkFromResponse = async (response: Response): Promise<string | null> => {
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
        return null;
    }

    const clonedResponse = response.clone();
    const attachmentDownloadResponse = (await clonedResponse.json()) as AttachmentDownloadResponse;
    if (!attachmentDownloadResponse.isLink) {
        return null;
    }

    return getSafeExternalUrl(attachmentDownloadResponse.linkUrl) || null;
};

export const getImageId = (attachmentId: string): string | undefined => {
    if (attachmentId) {
        const profileImagePath = attachmentId.split('/');
        return profileImagePath.pop();
    }
    return undefined;
};

export const fetchAttachmentUrl: FetchAttachmentUrl = async (
    attachmentId,
    activeOrganizationAccount,
    stampDocument
) => {
    if (isEmpty(attachmentId)) return '';

    const token = getToken();
    const headers = new Headers();
    headers.append('Authorization', token ? `Bearer ${token.token}` : '');
    if (activeOrganizationAccount) {
        headers.append(OWNER_ID, activeOrganizationAccount);
    }
    if (stampDocument ?? true) {
        headers.append(STAMP_METADATA_HEADER, 'true');
    }

    const imgUrl = attachmentId.includes(BASE_PATH)
        ? `${DOWNLOAD_URL}/${getImageId(attachmentId)}`
        : `${DOWNLOAD_URL}/${attachmentId}`;

    const response = await fetch(imgUrl, { headers });
    if (!response.ok) return '';

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
};

export const fetchAttachmentDownloadUrl: FetchAttachmentUrl = async (
    attachmentId,
    activeOrganizationAccount,
    stampDocument
) => {
    if (isEmpty(attachmentId)) return '';

    const token = getToken();
    const headers = new Headers();
    headers.append('Authorization', token ? `Bearer ${token.token}` : '');
    if (activeOrganizationAccount) {
        headers.append(OWNER_ID, activeOrganizationAccount);
    }
    if (stampDocument ?? true) {
        headers.append(STAMP_METADATA_HEADER, 'true');
    }

    const response = await fetch(`${ATTACHMENT_DOWNLOAD_URL}/${attachmentId}`, { headers });
    if (!response.ok) return '';

    const attachmentDownloadLink = await getAttachmentDownloadLinkFromResponse(response);
    if (attachmentDownloadLink) {
        return attachmentDownloadLink;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
};
